import { isVideoMediaUrl } from '@/shared/lib/media-proxy'
import { outputDataTypeForNode, type StudioFlowNode } from '@/features/studio/canvas/types'

export interface CreativePublishClipOption {
  key: string
  nodeId: string
  takeVersion: number
  label: string
  previewUrl: string
  isPreferred: boolean
  mediaType: 'video' | 'image'
}

function takeSucceeded(status?: string): boolean {
  const st = (status ?? '').trim()
  return st === '' || st === 'succeeded'
}

function clipKey(nodeId: string, takeVersion: number): string {
  return `${nodeId}:${takeVersion}`
}

function appendClip(
  out: CreativePublishClipOption[],
  seen: Set<string>,
  node: StudioFlowNode,
  takeVersion: number,
  previewUrl: string,
  mediaType: 'video' | 'image',
  titleSuffix?: string,
) {
  const url = previewUrl.trim()
  if (!url) return
  const dedupe = `${node.id}\0${url}`
  if (seen.has(dedupe)) return
  seen.add(dedupe)

  const baseLabel = node.data.label?.trim() || '画布片段'
  const label = titleSuffix ? `${baseLabel}${titleSuffix}` : baseLabel
  const preferred = node.data.preferredTakeVersion
  out.push({
    key: clipKey(node.id, takeVersion),
    nodeId: node.id,
    takeVersion,
    label,
    previewUrl: url,
    isPreferred: preferred != null && preferred > 0 ? takeVersion === preferred : false,
    mediaType,
  })
}

/** 从画布节点提取可发布片段（与 backend creative_publish 对齐） */
export function listCreativePublishClipOptions(nodes: StudioFlowNode[]): CreativePublishClipOption[] {
  const raw: CreativePublishClipOption[] = []
  const seen = new Set<string>()

  for (const node of nodes) {
    const nodeType = node.data.nodeType
    if (nodeType === 'group' || nodeType === 'context' || nodeType === 'text' || nodeType === 'prompt') {
      continue
    }
    if (node.data.register?.registeredAssetId) continue

    const preferred = node.data.preferredTakeVersion ?? 0

    if (nodeType === 'gen_image' || nodeType === 'gen_video') {
      const dataType = outputDataTypeForNode(nodeType)
      const mediaType: 'video' | 'image' = dataType === 'video' ? 'video' : 'image'
      const output = node.data.output
      if (output?.previewUrl && takeSucceeded(output.status)) {
        appendClip(raw, seen, node, preferred, output.previewUrl, mediaType)
      }
      for (const take of node.data.takes ?? []) {
        if (!take.previewUrl || !takeSucceeded(take.status)) continue
        const suffix = take.version > 0 ? ` · v${take.version}` : undefined
        appendClip(raw, seen, node, take.version, take.previewUrl, mediaType, suffix)
      }
      continue
    }

    if (nodeType === 'upload') {
      const uploadUrl = node.data.upload?.url
      if (uploadUrl) {
        const mediaType = isVideoMediaUrl(uploadUrl) ? 'video' : 'image'
        appendClip(raw, seen, node, 0, uploadUrl, mediaType)
      }
      const output = node.data.output
      if (output?.previewUrl) {
        const dt = output.dataType === 'video' || isVideoMediaUrl(output.previewUrl) ? 'video' : 'image'
        appendClip(raw, seen, node, 0, output.previewUrl, dt)
      }
      continue
    }

    if (nodeType === 'export') {
      const exportUrl = node.data.export?.outputUrl
      if (exportUrl && takeSucceeded(node.data.export?.status)) {
        appendClip(raw, seen, node, 0, exportUrl, 'video')
      }
    }
  }

  const videos = raw.filter((c) => c.mediaType === 'video')
  if (videos.length > 0) return videos
  return raw.filter((c) => c.mediaType === 'image')
}

/** 默认勾选：每个节点仅选首选 take，无首选则选最新版本 */
export function defaultSelectedCreativePublishKeys(
  clips: CreativePublishClipOption[],
  focusNodeId?: string | null,
): Set<string> {
  const byNode = new Map<string, CreativePublishClipOption[]>()
  for (const clip of clips) {
    if (focusNodeId && clip.nodeId !== focusNodeId) continue
    const list = byNode.get(clip.nodeId) ?? []
    list.push(clip)
    byNode.set(clip.nodeId, list)
  }
  if (focusNodeId && byNode.size === 0) {
    for (const clip of clips) {
      const list = byNode.get(clip.nodeId) ?? []
      list.push(clip)
      byNode.set(clip.nodeId, list)
    }
  }

  const selected = new Set<string>()
  for (const nodeClips of byNode.values()) {
    const preferred = nodeClips.find((c) => c.isPreferred)
    if (preferred) {
      selected.add(preferred.key)
      continue
    }
    let best = nodeClips[0]
    for (const c of nodeClips.slice(1)) {
      if (c.takeVersion > best.takeVersion) best = c
    }
    selected.add(best.key)
  }
  return selected
}

export function clipOptionsToPublishPayload(
  clips: CreativePublishClipOption[],
  selectedKeys: Set<string>,
): Array<{ canvas_node_id: string; take_version: number }> {
  return clips
    .filter((c) => selectedKeys.has(c.key))
    .map((c) => ({ canvas_node_id: c.nodeId, take_version: c.takeVersion }))
}
